import { TextureLoader, CubeTextureLoader, LoadingManager } from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader'
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader'
import { EventEmitter } from './utilities/EventEmitter'

export type Resource =
  | {
    key: string // point id
    status?: boolean // 监测点是否报警的标志，缺少该属性表示非监测点数据
    name: string // point name
    type: Exclude<AssetType, 'cubeTexture'>
    path: string
    position?: { px: number, py: number, pz: number, rx: number, ry: number, rz: number, sx: number, sy: number, sz: number }
  }
  | {
    key: string // point id
    name: string // point name
    type: 'cubeTexture'
    path: string[]
    position?: { px: number, py: number, pz: number, rx: number, ry: number, rz: number, sx: number, sy: number, sz: number }
  }

type AssetType = 'gltf' | 'texture' | 'cubeTexture'

type Loaders = {
  gltf: GLTFLoader
  texture: TextureLoader
  cubeTexture: CubeTextureLoader
}

export class Resources extends EventEmitter {
  private loadingManager = new LoadingManager(
    () => {
      this.emit('loaded')
    },
    // @ts-ignore
    (url: string, item: number, total: number) => {
      this.emit('progress', item / total)
    },
    (url: string) => {
      console.error(`Failed to load ${url}`)
    }
  )
  private loaders!: Loaders
  public items: Record<string, any> = {}

  constructor(private readonly resources: Resource[]) {
    super()
    this.initLoaders()
    this.load()
  }

  private initLoaders() {
    this.loaders = {
      gltf: new GLTFLoader(this.loadingManager),
      texture: new TextureLoader(this.loadingManager),
      cubeTexture: new CubeTextureLoader(this.loadingManager),
    }
  }

  getItem(key: string) {
    const item = this.items[key]
    if (!item) {
      throw new Error(`Resource ${key} not found`)
    }
    return item
  }

  deleteItem(...keys: string[]) {
    for (const key of keys) {
      const item = this.items[key]
      if (!item) {
        throw new Error(`Resource ${key} not found`)
      }
      delete this.items[key];
    }
  }

  addItem(...resources: Resource[]) {
    const result: Promise<any>[] = []
    for (const resource of resources) {
      if (resource.type === 'gltf') {
        result.push(new GLTFLoader(new LoadingManager(
          () => { },
          () => { },
          (url: string) => {
            console.error(`Failed to load ${url}`)
          }
        )).loadAsync(resource.path).then((file) => Promise.resolve(this.items[resource.key] = file.scene)))
      }
    }
    return Promise.all(result)
  }

  load() {
    if (this.resources.length === 0) {
      setTimeout(() => {
        this.emit('loaded')
      })
    }

    for (const resource of this.resources) {
      switch (resource.type) {
        case 'gltf':

          // 尝试加载使用 DRACOLoader 的模型
          const dracoLoader = new DRACOLoader();
          dracoLoader.setDecoderPath('/draco/'); // 设置解码器的路径 /public 路径
          this.loaders.gltf.setDRACOLoader(dracoLoader);
          this.loaders.gltf.load(
            resource.path,
            (file) => (this.items[resource.key] = file.scene)
          )
          break
        case 'texture':
          this.loaders.texture.load(
            resource.path,
            (file) => (this.items[resource.key] = file)
          )
          break
        case 'cubeTexture':
          this.loaders.cubeTexture.load(
            resource.path,
            (file) => (this.items[resource.key] = file)
          )
          break
      }
    }
  }
}
